import AdobeEvent from './AdobeEvent';
import CachedDataService from './services/CachedDataService';
import DataRetrievalService from './services/DataRetrievalService';
import Environment from './services/Environment';
import LocalStorageService from './services/LocalStorageService';
import LoggedOnStatusService from './services/LoggedOnStatusService';
import PageLoadDataService from './services/PageLoadDataService';
import UserDetailsService from './services/UserDetailsService';
import WindowService from './services/WindowService';

export default class RetAdobeLaunch {
  public readonly localStorageService = new LocalStorageService();
  public readonly userDetails = new UserDetailsService();
  public readonly loginStatus = new LoggedOnStatusService();
  public dataRetrievalService: DataRetrievalService = new DataRetrievalService();

  public async initializePage(data?: object, environment = 'production') {
    Environment.setLocation(environment);
    this.trackPageLoadEvent(data);
    await this.trackUserData(environment);
    this.trackLocalStorageEvents();
    this.trackPageLoadCompleted();
  }

  /*
   * This is only used in test files because of access issues probably?
   * */
  public getLocalStorageService(): LocalStorageService {
    return this.localStorageService;
  }

  public trackEvent(eventName: string, eventDataName?: string, eventData?: object) {
    const adobeEvent = new AdobeEvent(eventName, eventDataName, eventData);
    WindowService.pushToDatalayer(adobeEvent.getData());
  }

  public trackDelayedEvent(eventName: string, eventDataName?: string, eventData?: object) {
    const adobeEvent = new AdobeEvent(eventName, eventDataName, eventData);
    this.localStorageService.addEventDataItem(adobeEvent);
  }

  public async trackUserData(environment = 'production') {
    Environment.setLocation(environment);
    let dataResponse;
    const cachedData = CachedDataService.get();
    const isUserLoggedIn = !!cachedData.uid;

    if (isUserLoggedIn) {
      if (cachedData.isMinor === undefined) {
        dataResponse = await this.dataRetrievalService.get();
        const { isMinor } = dataResponse || { isMinor: false };
        cachedData.isMinor = isMinor;
      }

      if (!cachedData.isMinor) {
        cachedData.userInfo =
          cachedData.userInfo === undefined
            ? await this.userDetails.getUserInfo(dataResponse, LoggedOnStatusService.getLoginStatus(isUserLoggedIn))
            : cachedData.userInfo;
        this.trackUserDetected(cachedData.userInfo, isUserLoggedIn);
      }

      CachedDataService.set(cachedData);
    } else {
      this.trackUserDetected(cachedData.userInfo, isUserLoggedIn);
    }
  }

  public trackUserDetected(userInfo, isUserLoggedIn) {
    const eventToTrack = isUserLoggedIn
      ? userInfo
      : {
          loginStatus: LoggedOnStatusService.LOGGED_OUT,
        };
    this.trackEvent('userDetected', 'user', eventToTrack);
  }

  public trackLocalStorageEvents() {
    this.localStorageService.get().forEach((eventDataItem) => {
      WindowService.pushToDatalayer(eventDataItem);
    });
    this.localStorageService.clear();
  }

  public trackSignInClick(): void {
    this.userDetails.clearCache();
    this.trackDelayedEvent('userSignedIn', 'user', { signInClicked: true });
  }

  public trackPageLoadEvent(data) {
    const pageData = PageLoadDataService.buildPageData(data);
    this.trackEvent('pageLoaded', 'page', pageData);
  }

  public trackPageLoadCompleted() {
    this.trackEvent('pageLoadCompleted');
  }
}
